import React, { Fragment, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { login, newLogin } from "../../actions/auth";
import { useHistory } from "react-router-dom";
import googleIcon from "../../images/google-icon.svg";
import { useTranslation } from "react-i18next";
import { useLoading } from "../LoadingContext";
import { EyeIcon, EyeOffIcon, EmailIcon, LockIcon } from "../icons";
import { FiArrowRight } from "react-icons/fi";
import { useGoogleSignIn } from "../../hooks";
import { MotionModal, ModalCloseButton } from "../motion";

const LoginModal = ({
  show,
  handleAfterLogin,
  handleCloseModal,
  useAfterLoginFunc = false,
  redirectUrl = "/",
  isBooking = true
}) => {
  const { t } = useTranslation();
  const [loginFailed, setLoginFailed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [formDataRegister, setFormDataRegister] = useState({
    email: "",
    password: "",
    password_confirmation: "",
    sign_up_with_google: false,
    access_token_google: null,
    avatar: null,
  });

  const history = useHistory();
  const [isGoogleSignUp, setIsGoogleSignUp] = useState(false);
  const { showLoading, closeLoading } = useLoading();

  const { email, password } = formData;

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const res = await newLogin(email, password);

    if (res.status === 200) {
      if (useAfterLoginFunc) {
        handleAfterLogin();
      } else {
        window.location.reload();
      }
    } else {
      setLoginFailed(true);
    }
  };

  const handleGoogleCredential = async ({ credential, email: userEmail, picture: userImageURL }) => {
    try {
      // `credential` is the Google ID token (JWT) returned by GIS. The backend
      // verifies it and derives the email; the decoded email/picture are only
      // used for the registration prefill.
      showLoading(true);
      const checkLoginWithGoogle = await newLogin(
        userEmail,
        credential,
        "google"
      );

      closeLoading(true);

      if (checkLoginWithGoogle.status === 200) {
        // Existing Google user — logged in.
        await handleCloseModal();
        if (useAfterLoginFunc) {
          handleAfterLogin();
        } else {
          window.location.reload();
        }
      } else if (checkLoginWithGoogle?.data?.status === "USER_NOT_FOUND") {
        // New user — switch to the Google registration prefill (keep the modal
        // open so the register view shows).
        setFormDataRegister({
          ...formDataRegister,
          sign_up_with_google: true,
          email: userEmail,
          access_token_google: credential,
          avatar: userImageURL,
        });
        setIsGoogleSignUp(true);
      } else {
        setLoginFailed(true);
      }
    } catch (error) {
      console.error("Error signing in:", error);
    }
  };

  const { signIn: handleSignInGoogle, hiddenButtonRef } =
    useGoogleSignIn(handleGoogleCredential);


  return (
    <>
      {/* Off-screen Google Identity Services button, clicked programmatically
          by the visible "Continue with Google" button. Kept mounted so the GIS
          button can render regardless of modal visibility. */}
      <div
        ref={hiddenButtonRef}
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
      ></div>
      {/* Same popup shell as every other front-page modal (soft backdrop,
          rise-in, Esc / backdrop click to close, page scroll locked). */}
      <MotionModal show={show} onClose={handleCloseModal} maxWidthClassName="tw:max-w-[900px]">
                <div className="modal-body px-4 py-0 tw:pb-6 tw:lg:pb-0">
                  <div className="tw:flex tw:flex-col tw:lg:flex-row tw:lg:justify-between">
                    <div className="tw:w-full">
                      <div className="tw:!mt-9 tw:mb-6">
                        <div className="col-auto">
                          <div className="tw:font-hanken-grotesk tw:font-semibold tw:text-[#8D8D8D] tw:text-[8px] tw:tracking-[3px]">
                            {t("login.retruning_customer")}
                          </div>
                          <div className="tw:font-hanken-grotesk tw:text-[##212529] tw:text-lg tw:font-semibold tw:mt-1">
                            {t("login.login_signup")}
                          </div>
                          <div className="tw:border-b tw:border-b-[1px] tw:border-b-[#EEEEEE] tw:mt-6 tw:w-full"></div>
                        </div>
                      </div>
                      {/* START FORM */}
                      {loginFailed ? (
                        <>
                          {" "}
                          <div className="alert alert-danger">
                            {t("login.error")}
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                      <form onSubmit={(e) => onSubmit(e)} action="#">
                        <div className="tw:space-y-3">
                          <div className="tw:relative tw:border tw:border-[#C9C9C9] tw:rounded-md tw:h-[54px] tw:flex tw:items-center tw:px-[15px]">
                            <div className="tw:py-[10px] tw:w-[27px]">
                              <EmailIcon />
                            </div>
                            <div className="tw:relative tw:w-full tw:h-full tw:flex tw:items-center tw:py-[10px]">
                              <input
                                type="text"
                                id="login_modal_email"
                                name="email"
                                placeholder=" "
                                onChange={(e) => onChange(e)}
                                className="tw:block tw:appearance-none tw:peer tw:w-full tw:outline-none tw:!mt-4 tw:text-sm"
                              />
                              <label
                                htmlFor="login_modal_email"
                                className="tw:cursor-text tw:absolute tw:text-sm tw:font-hanken-grotesk tw:text-[#A7A7A7] tw:duration-300 tw:transform tw:-translate-y-4 tw:scale-75 tw:pt-2 tw:top-4 tw:z-10 tw:origin-[0]
                                                            tw:peer-placeholder-shown:scale-100 tw:peer-placeholder-shown:translate-y-0 tw:peer-placeholder-shown:pt-0
                                                            tw:peer-focus:scale-75 tw:peer-focus:-translate-y-4 tw:peer-focus:pt-2
                                                             tw:w-full
                                                            "
                              >
                                {t("login.email_address")}
                              </label>
                            </div>
                          </div>

                          <div className="tw:relative tw:border tw:border-[#C9C9C9] tw:rounded-md tw:h-[54px] tw:flex tw:items-center tw:px-[15px]">
                            <div className="tw:py-[10px] tw:w-[27px]">
                              <LockIcon />
                            </div>
                            <div className="tw:relative tw:w-full tw:h-full tw:flex tw:items-center tw:py-[10px]">
                              <input
                                type={showPassword ? "text" : "password"}
                                id="login_modal_password"
                                name="password"
                                placeholder=" "
                                onChange={(e) => onChange(e)}
                                className="tw:block tw:appearance-none tw:peer tw:w-full tw:outline-none tw:!mt-4 tw:text-sm"
                              />
                              <label
                                htmlFor="login_modal_password"
                                className="tw:cursor-text tw:absolute tw:text-sm tw:font-hanken-grotesk tw:text-[#A7A7A7] tw:duration-300 tw:transform tw:-translate-y-4 tw:scale-75 tw:pt-2 tw:top-4 tw:z-10 tw:origin-[0] 
                                  tw:peer-placeholder-shown:scale-100 tw:peer-placeholder-shown:translate-y-0 tw:peer-placeholder-shown:pt-0
                                  tw:peer-focus:scale-75 tw:peer-focus:-translate-y-4 tw:peer-focus:pt-2
                                   tw:w-full
                                  "
                              >
                                {t("login.password")}
                              </label>
                              <div
                                onClick={togglePasswordVisibility}
                                style={{
                                  position: 'absolute',
                                  right: '12px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  cursor: 'pointer',
                                  zIndex: 5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '4px'
                                }}
                              >
                                {showPassword ? (
                                  <EyeIcon />
                                ) : (
                                  <EyeOffIcon />
                                )}
                              </div>
                            </div>
                            {/* <a
                              href="/forgot-password"
                              className="tw:font-hanken-grotesk tw:!text-[#8D8D8D] tw:italic tw:text-sm"
                            >
                              {t("login.forgot_password")}
                            </a> */}
                          </div>
                        </div>
                        <a
                          href="/forgot-password"
                          className="tw:font-hanken-grotesk tw:!text-[#8D8D8D] tw:italic tw:text-sm tw:mt-4 tw:block"
                        >
                          {t("login.forgot_password")}
                        </a>
                        <div className="tw:!space-y-3 tw:mt-6 tw:text-center">
                          <button
                            name=""
                            id=""
                            type="submit"
                            className="tw:!w-full tw:!bg-[#D4B055] tw:h-[50px] tw:!rounded-md tw:!text-white tw:!flex tw:items-center tw:!justify-center tw:gap-[10px] tw:leading-5"
                            href="#"
                            role="button"
                          >
                            <div className="tw:font-hanken-grotesk tw:font-bold tw:text-sm">
                              {t("login.login")}
                            </div>
                            <FiArrowRight size={24} />
                          </button>
                          <div>
                            <a
                              className="tw:!no-underline tw:hidden tw:md:block"
                              data-bs-dismiss="modal"
                              data-bs-toggle="modal"
                              data-bs-target="#register"
                              style={{ fontSize: "14px" }}
                              onClick={handleCloseModal}
                            >
                              <span
                                className="tw:font-hanken-grotesk tw:text-[#212529] tw:cursor-pointer"
                                dangerouslySetInnerHTML={{
                                  __html: t("login.sign_up_link"),
                                }}
                              ></span>
                            </a>
                            <a
                              className="tw:!no-underline tw:block tw:md:hidden"
                              style={{ fontSize: "14px" }}
                              onClick={() => {
                                handleCloseModal();
                                history.push("/login?is_register=true");
                              }}
                            >
                              <span
                                className="tw:font-hanken-grotesk tw:text-[#212529]"
                                dangerouslySetInnerHTML={{
                                  __html: t("login.sign_up_link"),
                                }}
                              ></span>
                            </a>
                          </div>
                        </div>
                      </form>

                      <div className="tw:flex tw:items-center tw:space-x-4 tw:mt-6">
                        <div className="tw:bg-[#EEEEEE] tw:h-[1px] tw:flex-1"></div>
                        <div className="tw:font-hanken-grotesk tw:text-[#212529] tw:text-sm tw:whitespace-nowrap tw:flex-shrink-0 tw:capitalize">
                          {t("login.or_google")}
                        </div>
                        <div className="tw:bg-[#EEEEEE] tw:h-[1px] tw:flex-1"></div>
                      </div>

                      <div className="justify-content-center tw:mt-6 tw:mb-9">
                        <div>
                          <a
                            name=""
                            id=""
                            className="tw:!h-[50px] tw:flex tw:items-center tw:justify-center tw:rounded-md tw:border tw:border-[#8D8D8D] tw:!no-underline tw:space-x-[10px]"
                            role="button"
                            onClick={handleSignInGoogle}
                          >
                            <img
                              src={googleIcon}
                              className="img-fluid"
                              alt="Efficiency"
                            />
                            <span className="tw:font-hanken-grotesk tw:text-[#282828] tw:text-sm">
                              {t("login.continue_w_google")}
                            </span>
                          </a>
                        </div>
                      </div>
                      {/* END FORM */}
                    </div>
                    <div className="tw:lg:px-6">
                      <div className="line-separator-section">
                        {/* <div className="circle noto">{t("login.or")}</div> */}
                        <div className="tw:z-10 tw:font-hanken-grotesk tw:rounded-full tw:w-[42px] tw:h-[42px] tw:border tw:border-[#8D8D8D] tw:flex tw:items-center tw:justify-center tw:bg-white tw:text-[#8D8D8D] tw:font-medium">
                          {t("login.or")}
                        </div>
                        <div className="horizontal-line"></div>
                        <div className="tw:absolute tw:top-5 tw:lg:top-0 tw:bottom-0 tw:left-0 tw:lg:left-1/2 tw:w-full tw:lg:w-[1px] tw:h-[1px] tw:lg:h-full tw:bg-[#D9D9D9]"></div>
                      </div>
                    </div>
                    <div className="tw:w-full tw:flex tw:flex-col">
                      <ModalCloseButton onClick={handleCloseModal} />
                      {isBooking && (
                        <div
                          className="tw:flex-1 tw:flex tw:justify-center tw:flex-col tw:mt-6 tw:lg:mt-0"
                          style={{ height: "100%" }}
                        >
                          <div className="tw:font-hanken-grotesk tw:text-[#8D8D8D] tw:font-semibold tw:tracking-[3px] tw:text-[8px]">
                            {t("login.guest_booking")}
                          </div>
                          <div className="tw:text-[#212529] tw:font-semibold tw:text-lg tw:capitalize">
                            {t("login.continue_as_guest")}
                          </div>
                          <div className="tw:mt-6 tw:mb-4">
                            <button
                              type="button"
                              name=""
                              id=""
                              className="tw:h-[50px] tw:!border tw:!border-[#8D8D8D] tw:!rounded-md tw:w-full tw:flex tw:items-center tw:justify-center tw:gap-[10px]"
                              href="#"
                              role="button"
                              onClick={() => {
                                if (useAfterLoginFunc) {
                                  handleAfterLogin();
                                } else {
                                  window.location.reload();
                                }
                              }}
                              data-bs-dismiss="modal"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="19"
                                height="18"
                                viewBox="0 0 19 18"
                                fill="none"
                              >
                                <path
                                  d="M9.5 2.51875C10.805 2.51875 11.8625 3.5175 11.8625 4.75C11.8625 5.9825 10.805 6.98125 9.5 6.98125C8.195 6.98125 7.1375 5.9825 7.1375 4.75C7.1375 3.5175 8.195 2.51875 9.5 2.51875ZM9.5 12.0812C12.8413 12.0812 16.3625 13.6325 16.3625 14.3125V15.4813H2.6375V14.3125C2.6375 13.6325 6.15875 12.0812 9.5 12.0812ZM9.5 0.5C7.01375 0.5 5 2.40188 5 4.75C5 7.09812 7.01375 9 9.5 9C11.9862 9 14 7.09812 14 4.75C14 2.40188 11.9862 0.5 9.5 0.5ZM9.5 10.0625C6.49625 10.0625 0.5 11.4862 0.5 14.3125V17.5H18.5V14.3125C18.5 11.4862 12.5037 10.0625 9.5 10.0625Z"
                                  fill="#8D8D8D"
                                />
                              </svg>

                              <div className="tw:text-[#282828] tw:font-hanken-grotesk tw:capitalize tw:font-normal">
                                {t("login.continue_as_guest")}
                              </div>
                            </button>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>
      </MotionModal>
    </>
  );
};

LoginModal.propTypes = {
  login: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps, { login })(LoginModal);
